import React, { ReactNode } from "react";
import Grid from "@mui/material/Grid";

export interface MGridLayoutItem {
  /** The component to render */
  component: ReactNode;
  /** Grid size (1-12) for horizontal/full-screen view. Default is equal distribution in the row */
  size?: number;
  /** Grid size for vertical/narrow view. Default is 12 (full width) */
  verticalSize?: number;
  /** Whether the component should stretch to fill the row height. Default is true */
  stretchHeight?: boolean;
}

export interface MGridLayoutProps {
  /** 
   * Array of rows. Each row is an array of items to display in that row.
   * In vertical mode, all items stack full-width regardless of row grouping.
   */
  rows: (MGridLayoutItem | ReactNode)[][];
  /** When true, all items display full-width stacked vertically */
  verticalDisplay?: boolean;
  /** Grid spacing. Default is 1 */
  spacing?: number;
  /** Padding around the grid container. Default is 0 */
  padding?: number;
  /** 
   * Optional children to render before the rows. 
   * Useful for components that return their own Grid items.
   */
  children?: ReactNode;
  /** Optional className for the grid container */
  className?: string;
}

// Flex styles to make items stretch to fill row height
const stretchStyles = { display: 'flex', '& > *': { flex: 1 } };

/**
 * A simple grid layout component that renders rows of components.
 * 
 * Usage:
 * ```tsx
 * <MGridLayout
 *   verticalDisplay={false}
 *   rows={[
 *     [{ component: <Card1 /> }, { component: <Card2 /> }, { component: <Card3 /> }],  // Row 1: 3 equal cards
 *     [{ component: <Card4 />, size: 8 }, { component: <Card5 />, size: 4 }],          // Row 2: 8/4 split
 *     [{ component: <Card6 />, stretchHeight: false }],                                 // Row 3: don't stretch height
 *   ]}
 * />
 * ```
 */
const MGridLayout = ({ rows, verticalDisplay = false, spacing = 1, padding = 0, children, className }: MGridLayoutProps) => {
  
  const normalizeItem = (item: MGridLayoutItem | ReactNode): MGridLayoutItem => {
    // If it's just a ReactNode (not an object with component property), wrap it
    if (React.isValidElement(item) || item === null || item === undefined) {
      return { component: item as ReactNode };
    }
    return item as MGridLayoutItem;
  };

  const getItemSize = (item: MGridLayoutItem, rowLength: number): number => {
    if (verticalDisplay) {
      return item.verticalSize ?? 12;
    }
    // If size is specified, use it; otherwise distribute equally
    return item.size ?? Math.floor(12 / rowLength);
  };

  const getItemSx = (item: MGridLayoutItem) => {
    // Default to stretching unless explicitly disabled
    return item.stretchHeight !== false ? stretchStyles : undefined;
  };

  if (verticalDisplay) {
    // In vertical mode, flatten all rows and render each item full-width
    const allItems = rows.flat().map(normalizeItem);
    return (
      <Grid container spacing={spacing} sx={{ padding }} className={className}>
        {children}
        {allItems.map((item, index) => (
          <Grid key={index} size={item.verticalSize ?? 12} sx={getItemSx(item)}>
            {item.component}
          </Grid>
        ))}
      </Grid>
    );
  }

  // Horizontal mode: render rows as defined
  return (
    <Grid container spacing={spacing} sx={{ padding }} className={className}>
      {children}
      {rows.map((row, rowIndex) => (
        <React.Fragment key={rowIndex}>
          {row.map((rawItem, itemIndex) => {
            const item = normalizeItem(rawItem);
            const size = getItemSize(item, row.length);
            return (
              <Grid key={`${rowIndex}-${itemIndex}`} size={size} sx={getItemSx(item)}>
                {item.component}
              </Grid>
            );
          })}
        </React.Fragment>
      ))}
    </Grid>
  );
};

export default MGridLayout;
